import React, { useState, useEffect } from 'react';
import { isEqual, cloneDeep } from 'lodash';
import { Switch, Input, InputNumber, Select, Tooltip, Icon, Button, Upload, message } from 'antd';
import logoimg from '../../assets/img/logo_one.png';
import logoorg from '../../assets/img/logo.png';
import logonj from '../../assets/img/logo_nj.png';
import whlogo from '../../assets/img/Workhorse_Logo.png';
import ghlogo from '../../assets/img/GH_Logo-latest.png';
import { uploadLogo } from '../../_shared/services/api.service';

const LOGO_PREVIEWS = {
  new: logoimg,
  orig: logoorg,
  nj: logonj,
  wh: whlogo,
  gh: ghlogo,
};

const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const LogoUploadField = ({ schema, value, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const beforeUpload = async (file) => {
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      message.error('Only PNG, JPG, SVG, or WEBP images are allowed.');
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_LOGO_BYTES) {
      message.error('File must be under 2 MB.');
      return Upload.LIST_IGNORE;
    }
    try {
      setUploading(true);
      const dataUrl = await fileToBase64(file);
      const url = await uploadLogo({ data: dataUrl, size: schema.size, contentType: file.type });
      if (!url) throw new Error('Upload returned no URL');
      onChange(url);
      message.success('Logo uploaded');
    } catch (err) {
      message.error(`Upload failed: ${err.message || 'unknown error'}`);
    } finally {
      setUploading(false);
    }
    return Upload.LIST_IGNORE;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      {value && (
        <div style={{ width: 80, height: 40, border: '1px solid #e8e8e8', borderRadius: 4, padding: 4, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <img src={value} alt="logo preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 180 }}>
        <Input
          value={value || ''}
          onChange={e => onChange(e.target.value || undefined)}
          placeholder="Upload below, or paste a public image URL"
        />
      </div>
      <Upload beforeUpload={beforeUpload} showUploadList={false} accept={ALLOWED_LOGO_TYPES.join(',')}>
        <Button loading={uploading}>
          <Icon type="upload" /> Upload
        </Button>
      </Upload>
    </div>
  );
};

const { TextArea } = Input;
const { Option } = Select;

const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
// Keys whose string values must be web URLs
const URL_KEYS = ['externalUrl', 'account.toslink'];
// FAQ entries are rendered straight into an <a href>, so only http(s) links
// are accepted — a config value must never become a javascript: URL.
const FAQ_URL_RE = /^https?:\/\//i;

// Per-type (plus a few per-key) validation. Returns an error string, or null if valid.
// `undefined` is always valid — it means "not set, use the default".
export const validateSetting = (schema, value) => {
  if (value === undefined) return null;

  switch (schema.type) {
    case 'boolean':
      if (typeof value !== 'boolean') return 'Must be true or false.';
      return null;

    case 'number':
      if (typeof value !== 'number' || !isFinite(value)) return 'Must be a number.';
      return null;

    case 'string':
    case 'html': {
      if (typeof value !== 'string') return 'Must be text.';
      if (!value) return null;
      if (URL_KEYS.includes(schema.key) && !/^https?:\/\//i.test(value)) {
        return 'Must be a URL starting with http:// or https://.';
      }
      if (schema.type === 'string' && /email$/i.test(schema.key) && !EMAIL_RE.test(value)) {
        return 'Must be a valid email address.';
      }
      if (schema.key.indexOf('defaultLanding.') === 0 && value[0] !== '/') {
        return "Must be a dashboard path starting with '/' (e.g. /dashboard/admin).";
      }
      return null;
    }

    case 'select':
    case 'logoSelect':
      if (typeof value !== 'string') return 'Must be one of the listed options.';
      return null;

    case 'logoUpload':
      if (typeof value !== 'string') return 'Must be an image URL.';
      if (value && !/^(https?:\/\/|data:|\/)/i.test(value)) {
        return 'Must be an image URL (http(s)://...), a data: URI, or an absolute path.';
      }
      return null;

    case 'stringArray':
    case 'daysOfWeek': {
      if (!Array.isArray(value) || value.some(v => typeof v !== 'string')) {
        return 'Must be a list of text values.';
      }
      if (/email/i.test(schema.key)) {
        const bad = value.filter(v => !EMAIL_RE.test(v));
        if (bad.length) return `Invalid email address${bad.length > 1 ? 'es' : ''}: ${bad.join(', ')}`;
      }
      return null;
    }

    case 'faqCustom': {
      if (!Array.isArray(value)) return 'Must be a list of FAQ entries.';
      for (let i = 0; i < value.length; i++) {
        const item = value[i];
        const where = `Entry ${i + 1}`;
        if (!isPlainObject(item)) return `${where}: must have a title and a link.`;
        if (typeof item.title !== 'string' || !item.title.trim()) return `${where}: a title is required.`;
        if (!FAQ_URL_RE.test(`${item.link || ''}`.trim())) {
          return `${where} ("${item.title.trim()}"): link must start with http:// or https://.`;
        }
      }
      return null;
    }

    case 'faqOverrides': {
      if (!isPlainObject(value)) return 'Must be a JSON object keyed by FAQ item id.';
      const ids = Object.keys(value);
      for (let i = 0; i < ids.length; i++) {
        const entry = value[ids[i]];
        if (!isPlainObject(entry)) return `'${ids[i]}': must have a title and/or a link.`;
        if (entry.title !== undefined && typeof entry.title !== 'string') return `'${ids[i]}': title must be text.`;
        if (entry.link !== undefined && `${entry.link}`.trim() && !FAQ_URL_RE.test(`${entry.link}`.trim())) {
          return `'${ids[i]}': link must start with http:// or https://.`;
        }
      }
      return null;
    }

    case 'json':
      // Registered json settings are all objects; unknown keys edited as raw
      // JSON may legitimately be arrays.
      if (!isPlainObject(value) && !(schema.isUnknown && Array.isArray(value))) {
        return 'Must be a JSON object (e.g. { "key": "value" }).';
      }
      return null;

    case 'menuOverrides':
      if (!isPlainObject(value)) return 'Must be a JSON object (e.g. { "key": "value" }).';
      return null;

    default:
      return null;
  }
};

const DaysOfWeekField = ({ value, onChange }) => {
  const selected = Array.isArray(value) ? value.map(d => `${d}`.toUpperCase()) : [];
  const toggle = (day) => {
    const next = selected.includes(day)
      ? selected.filter(d => d !== day)
      : [...selected, day];
    onChange(next.length ? DAYS_OF_WEEK.filter(d => next.includes(d)) : undefined);
  };
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {DAYS_OF_WEEK.map(day => {
        const isOn = selected.includes(day);
        return (
          <Button
            key={day}
            size="small"
            type={isOn ? 'primary' : 'default'}
            onClick={() => toggle(day)}
            style={{ minWidth: 52 }}
          >
            {day}
          </Button>
        );
      })}
    </div>
  );
};

const isBlankFaqRow = (r) => !`${(r && r.title) || ''}`.trim() && !`${(r && r.link) || ''}`.trim();

// Repeatable Title + Link rows for `faq.custom`. Rows are held locally so a
// freshly-added (still empty) row can exist in the UI without being written
// into the saved config; only filled-in rows are pushed up through onChange.
const FaqCustomField = ({ value, onChange }) => {
  const toRows = (v) => (Array.isArray(v) ? v : []).map(r => ({ title: (r && r.title) || '', link: (r && r.link) || '' }));
  const [rows, setRows] = useState(() => toRows(value));

  // Re-sync when the value changes externally (Discard Changes / Reset /
  // Raw JSON edits). Locally-typed blank rows never reach `value`, so compare
  // against the non-blank rows to avoid wiping a row being filled in.
  useEffect(() => {
    setRows(prev => (isEqual(prev.filter(r => !isBlankFaqRow(r)), toRows(value)) ? prev : toRows(value)));
  }, [value]);

  const commit = (next) => {
    setRows(next);
    const filled = next.filter(r => !isBlankFaqRow(r));
    onChange(filled.length ? filled : undefined);
  };

  const setField = (i, field, val) => commit(rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeRow = (i) => commit(rows.filter((r, idx) => idx !== i));
  const moveRow = (i, delta) => {
    const target = i + delta;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    next[i] = rows[target];
    next[target] = rows[i];
    commit(next);
  };

  return (
    <div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          <Input
            value={row.title}
            placeholder="Title (e.g. Required Payments)"
            onChange={e => setField(i, 'title', e.target.value)}
            style={{ flex: '1 1 200px', minWidth: 160 }}
          />
          <Input
            value={row.link}
            placeholder="https://docs.google.com/..."
            onChange={e => setField(i, 'link', e.target.value)}
            style={{ flex: '2 1 260px', minWidth: 200 }}
          />
          <Button size="small" icon="up" disabled={i === 0} onClick={() => moveRow(i, -1)} title="Move up" />
          <Button size="small" icon="down" disabled={i === rows.length - 1} onClick={() => moveRow(i, 1)} title="Move down" />
          <Button size="small" type="danger" icon="delete" onClick={() => removeRow(i)} title="Remove" />
        </div>
      ))}
      <Button icon="plus" onClick={() => commit([...rows, { title: '', link: '' }])} style={{ marginTop: rows.length ? 4 : 0 }}>
        Add FAQ Item
      </Button>
    </div>
  );
};

const truncate = (v, max) => (`${v}`.length > max ? `${`${v}`.slice(0, max - 1)}\u2026` : `${v}`);

// Per-client title/link replacements for the built-in FAQ entries. One row per
// entry in the FAQ page source; an empty box means "keep the built-in value",
// which is what the placeholder shows.
const FaqOverridesField = ({ schema, value, onChange }) => {
  const overrides = isPlainObject(value) ? value : {};

  const setField = (id, field, val) => {
    const next = { ...overrides };
    const entry = { ...(next[id] || {}) };
    if (!val) {
      delete entry[field];
    } else {
      entry[field] = val;
    }
    if (Object.keys(entry).length === 0) {
      delete next[id];
    } else {
      next[id] = entry;
    }
    onChange(Object.keys(next).length ? next : undefined);
  };

  return (
    <div>
      {(schema.options || []).map(opt => {
        const entry = overrides[opt.value] || {};
        return (
          <div key={opt.value} style={{ marginBottom: 8 }}>
            <code style={{ fontSize: 11, color: '#888' }}>{opt.value}</code>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
              <Input
                value={entry.title || ''}
                placeholder={`Default: ${opt.label}`}
                onChange={e => setField(opt.value, 'title', e.target.value)}
                style={{ flex: '1 1 200px', minWidth: 160 }}
              />
              <Input
                value={entry.link || ''}
                placeholder={truncate(opt.link || 'https://...', 52)}
                onChange={e => setField(opt.value, 'link', e.target.value)}
                style={{ flex: '2 1 260px', minWidth: 200 }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const serializeJson = (v) => (v !== undefined ? JSON.stringify(v, null, 2) : '');

const JsonField = ({ value, onChange }) => {
  const [text, setText] = useState(serializeJson(value));
  const [error, setError] = useState(null);

  // Keep the textarea in sync when the value changes externally
  // (Discard Changes / Reset to Saved / Raw JSON edits).
  useEffect(() => {
    setText(prev => {
      let same = false;
      try {
        same = (!prev.trim() && value === undefined) || isEqual(JSON.parse(prev), value);
      } catch (e) { same = false; }
      return same ? prev : serializeJson(value);
    });
    setError(null);
  }, [value]);

  const handleBlur = () => {
    if (!text.trim()) {
      setError(null);
      onChange(undefined);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      setError(null);
      onChange(parsed);
    } catch (e) {
      setError('Invalid JSON');
    }
  };

  return (
    <div>
      <TextArea
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={handleBlur}
        rows={4}
        style={{ fontFamily: 'monospace', fontSize: 12 }}
      />
      {error && <div style={{ color: '#f5222d', fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
};

// Human-readable preview of a built-in default for the "Not set" notice.
const formatDefault = (val) => {
  const str = typeof val === 'string' ? val : JSON.stringify(val);
  return str.length > 160 ? `${str.slice(0, 157)}...` : str;
};

export const SettingField = ({ schema, value, onChange }) => {
  const isSet = value !== undefined;
  const defaultText = schema.defaultValue !== undefined ? `Default: ${JSON.stringify(schema.defaultValue)}` : null;
  const validationError = validateSetting(schema, value);

  const handleReset = () => {
    onChange(undefined);
  };

  const renderControl = () => {
    switch (schema.type) {
      case 'boolean':
        return (
          <Switch
            checked={isSet ? value : !!schema.defaultValue}
            onChange={checked => onChange(checked)}
          />
        );

      case 'string':
        return (
          <Input
            value={value || ''}
            onChange={e => onChange(e.target.value || undefined)}
            placeholder={schema.defaultValue || ''}
          />
        );

      case 'html':
        return (
          <div>
            <TextArea
              value={value || ''}
              onChange={e => onChange(e.target.value || undefined)}
              rows={3}
              placeholder={schema.defaultValue || ''}
            />
            <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>Supports HTML</div>
          </div>
        );

      case 'number':
        return (
          <InputNumber
            value={value}
            onChange={val => onChange(val)}
            placeholder={schema.defaultValue !== undefined ? String(schema.defaultValue) : ''}
            style={{ width: '100%' }}
          />
        );

      case 'select':
        return (
          <Select
            value={value}
            onChange={val => onChange(val)}
            placeholder={schema.defaultValue || 'Select...'}
            allowClear
            style={{ width: '100%' }}
          >
            {(schema.options || []).map(opt => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        );

      case 'logoUpload':
        return <LogoUploadField schema={schema} value={value} onChange={onChange} />;

      case 'logoSelect': {
        const effective = value || schema.defaultValue;
        const preview = LOGO_PREVIEWS[effective];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Select
              value={value}
              onChange={val => onChange(val)}
              placeholder={schema.defaultValue || 'Select...'}
              allowClear
              style={{ flex: 1 }}
            >
              {(schema.options || []).map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
            {preview && (
              <div style={{ width: 80, height: 40, border: '1px solid #e8e8e8', borderRadius: 4, padding: 4, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <img src={preview} alt="logo preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
            )}
          </div>
        );
      }

      case 'stringArray':
        return (
          <Select
            mode="tags"
            value={Array.isArray(value) ? value : []}
            onChange={val => onChange(val.length ? val : undefined)}
            placeholder={schema.options ? 'Select or type to add...' : 'Type and press Enter to add...'}
            style={{ width: '100%' }}
            tokenSeparators={[',']}
          >
            {(schema.options || []).map(opt => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        );

      case 'menuOverrides': {
        const overrides = value && typeof value === 'object' ? value : {};
        const handleTitleChange = (path, title) => {
          const next = { ...overrides };
          const entry = { ...(next[path] || {}) };
          if (!title) {
            delete entry.title;
          } else {
            entry.title = title;
          }
          if (Object.keys(entry).length === 0) {
            delete next[path];
          } else {
            next[path] = entry;
          }
          onChange(Object.keys(next).length ? next : undefined);
        };
        return (
          <div>
            {(schema.options || []).map(opt => (
              <div key={opt.value} style={{ display: 'flex', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                <code style={{ fontSize: 11, color: '#888', minWidth: 220 }}>{opt.value}</code>
                <Input
                  value={(overrides[opt.value] && overrides[opt.value].title) || ''}
                  placeholder={`Default: ${opt.label}`}
                  onChange={e => handleTitleChange(opt.value, e.target.value)}
                  style={{ flex: 1, minWidth: 180 }}
                />
              </div>
            ))}
          </div>
        );
      }

      case 'faqCustom':
        return <FaqCustomField value={value} onChange={onChange} />;

      case 'faqOverrides':
        return <FaqOverridesField schema={schema} value={value} onChange={onChange} />;

      case 'json':
        return <JsonField value={value} onChange={onChange} />;

      case 'daysOfWeek':
        return <DaysOfWeekField value={value} onChange={onChange} />;

      default:
        return (
          <Input
            value={typeof value === 'string' ? value : JSON.stringify(value)}
            onChange={e => onChange(e.target.value || undefined)}
          />
        );
    }
  };

  return (
    <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ marginBottom: 4 }}>
        <label style={{ fontWeight: 500 }}>
          {schema.label}
          <Tooltip title={`Config key: ${schema.key}`}>
            <code style={{ marginLeft: 8, fontSize: 11, color: '#999', fontWeight: 'normal' }}>{schema.key}</code>
          </Tooltip>
        </label>
      </div>
      <div style={{ marginBottom: 4 }}>
        {renderControl()}
      </div>
      {!isSet && schema.defaultValue !== undefined && (
        <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '6px 10px', fontSize: 12, color: '#874d00', marginBottom: 6, lineHeight: 1.5 }}>
          <Icon type="info-circle" style={{ marginRight: 6 }} />
          Not set — the app is using the built-in default:{' '}
          <code style={{ fontSize: 11, wordBreak: 'break-word' }}>{formatDefault(schema.defaultValue)}</code>
          <a
            onClick={() => onChange(cloneDeep(schema.defaultValue))}
            style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
          >
            <Icon type="edit" /> Customize
          </a>
        </div>
      )}
      {validationError && (
        <div style={{ color: '#f5222d', fontSize: 12, marginBottom: 4 }}>
          <Icon type="exclamation-circle" style={{ marginRight: 4 }} />
          {validationError}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: 2, flexWrap: 'wrap' }}>
        <div style={{ color: '#888', fontSize: 12, flex: 1, minWidth: 200 }}>
          {schema.description}
          {defaultText && <span style={{ marginLeft: 8, color: '#aaa' }}>({defaultText})</span>}
        </div>
        {isSet && (
          <a
            onClick={handleReset}
            style={{ fontSize: 12, marginLeft: 12, whiteSpace: 'nowrap', color: schema.isUnknown ? '#f5222d' : undefined }}
          >
            <Icon type={schema.isUnknown ? 'delete' : 'undo'} /> {schema.isUnknown ? 'Remove' : 'Reset'}
          </a>
        )}
      </div>
    </div>
  );
};
